import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../../components/ui/input-otp';
import { toast } from 'sonner';
import { Mail, LockKeyhole } from 'lucide-react';
import { formatApiErrorDetail } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ClientVerification() {
  const { token } = useParams();
  const [step, setStep] = useState('preview');
  const [invite, setInvite] = useState(null);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingOtp, setSendingOtp] = useState(false);
  const navigate = useNavigate();

  const loadInvitePreview = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/client/invite/${token}`);
      setInvite(data);
      if (data.status === 'opened') {
        setStep('otp');
      }
    } catch (e) {
      const errorMsg = formatApiErrorDetail(e.response?.data?.detail);
      toast.error(errorMsg);
      setTimeout(() => navigate('/'), 3000);
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    loadInvitePreview();
  }, [loadInvitePreview]);

  const handleSendOTP = async () => {
    setSendingOtp(true);
    try {
      await axios.post(`${API}/client/invite/${token}/send-otp`);
      toast.success('Verification code sent to your email');
      setStep('otp');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    try {
      const { data } = await axios.post(`${API}/client/invite/${token}/verify-otp`, { token, otp });
      toast.success('Verified! Starting your client profile...');
      navigate(`/client/form/${data.invite_id}`, { state: { token, draft: data.draft } });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setOtp('');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5b93d6]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 36, 68, 0.85), rgba(30, 58, 99, 0.85)), url('https://images.pexels.com/photos/12696432/pexels-photo-12696432.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-center items-center text-white p-12">
          <h1 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '0.05em' }}>
            <span style={{ fontWeight: 800 }}>STEELPEAK</span>{' '}
            <span style={{ fontWeight: 300 }}>WEALTH</span>
          </h1>
          <p className="text-xl text-center" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="client-profile-brand-title">
            Client Profile
          </p>
          <p className="text-sm text-center mt-4 opacity-90">
            Secure client intake platform
          </p>
        </div>
      </div>

      {/* Right side - Verification */}
      <div className="flex-1 min-w-0 flex items-center justify-center p-4 sm:p-8 bg-[#f8fafc]">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-6 sm:p-8">
            <section
              aria-labelledby="client-security-title"
              className="mb-6 rounded-md border border-[#5b93d6]/30 bg-[#5b93d6]/10 p-4"
              data-testid="client-security-notice"
            >
              <div className="flex items-center gap-2 mb-2">
                <LockKeyhole className="w-4 h-4 shrink-0 text-[#5b93d6]" aria-hidden="true" />
                <h2 id="client-security-title" className="text-base font-semibold text-[#0f172a]" data-testid="client-security-title">
                  Secure profile submission
                </h2>
              </div>
              <p className="text-sm leading-relaxed text-[#475569]" data-testid="client-security-description">
                Your profile is sent over an encrypted HTTPS connection. You'll verify your email before you begin.
              </p>
              <p className="mt-2 text-xs leading-relaxed text-[#475569]" data-testid="client-security-privacy">
                For your privacy, please do not forward your invitation link.
              </p>
            </section>
            {step === 'preview' && (
              <div data-testid="preview-step">
                <div className="mb-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
                    <img src="/stpk-logo.jpg" alt="SteelPeak" className="w-16 h-16 rounded-lg shadow-sm" data-testid="brand-logo" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#0f172a] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Verify Your Identity
                  </h2>
                  <p className="text-[#475569]" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="client-invitation-description">
                    You've been invited to complete your client profile
                  </p>
                </div>

                <div className="space-y-4 mb-8">
                  <div className="p-4 bg-[#f8fafc] rounded-md border border-[#e2e8f0]">
                    <p className="text-sm text-[#475569] mb-1">Email:</p>
                    <p className="text-sm font-medium text-[#0f172a]" data-testid="masked-email">
                      {invite?.client_email_masked}
                    </p>
                  </div>

                  {invite?.client_name && (
                    <div className="p-4 bg-[#f8fafc] rounded-md border border-[#e2e8f0]">
                      <p className="text-sm text-[#475569] mb-1">Name:</p>
                      <p className="text-sm font-medium text-[#0f172a]" data-testid="client-name">
                        {invite.client_name}
                      </p>
                    </div>
                  )}

                  <div className="p-4 bg-[#f8fafc] rounded-md border border-[#e2e8f0]">
                    <p className="text-sm text-[#475569] mb-1">Your Advisor:</p>
                    <p className="text-sm font-medium text-[#0f172a]" data-testid="advisor-info">
                      {invite?.advisor_name}
                    </p>
                    <p className="text-sm text-[#475569] mt-1">{invite?.advisor_phone}</p>
                  </div>
                </div>

                <Button
                  onClick={handleSendOTP}
                  disabled={sendingOtp}
                  className="w-full bg-gradient-to-r from-[#0f2444] via-[#1e3a63] to-[#26518c] hover:opacity-90 text-white font-medium"
                  data-testid="send-otp-button"
                >
                  {sendingOtp ? 'Sending...' : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send Verification Code
                    </>
                  )}
                </Button>
              </div>
            )}

            {step === 'otp' && (
              <div data-testid="otp-step">
                <div className="mb-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#5b93d6]/10 rounded-full mb-4">
                    <Mail className="w-8 h-8 text-[#5b93d6]" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#0f172a] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Enter Verification Code
                  </h2>
                  <p className="text-[#475569]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    We sent a 6-digit code to {invite?.client_email_masked}
                  </p>
                </div>

                <div className="mb-8 flex justify-center">
                  <InputOTP maxLength={6} value={otp} onChange={(value) => setOtp(value)} data-testid="otp-input">
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                <Button
                  onClick={handleVerifyOTP}
                  disabled={otp.length !== 6}
                  className="w-full bg-gradient-to-r from-[#0f2444] via-[#1e3a63] to-[#26518c] hover:opacity-90 text-white font-medium mb-4"
                  data-testid="verify-otp-button"
                >
                  Verify & Continue
                </Button>

                <Button
                  onClick={handleSendOTP}
                  variant="outline"
                  className="w-full"
                  data-testid="resend-otp-button"
                >
                  Resend Code
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
